<template>
  <SidebarComponent />

  <div class="dashboard-layout">
    <div class="content-container">
      <div class="card-container">
        <div class="titolo-card">
          <h1>Rate manuali</h1>
          <p class="subtitle">Visualizza e gestisci tutte le rate collegate agli ordini manuali.</p>
        </div>

        <section class="card-surface import-card">
          <div class="import-row">
            <div class="import-copy">
              <h3>Importa rate da CSV</h3>
              <p class="muted">
                Formato atteso: intestazioni con separatore ; (es. subscription_id;email;importo;data_attesa;stato;pagata).
              </p>
              <p v-if="importState.fileName" class="muted">File selezionato: {{ importState.fileName }}</p>
              <p v-if="importState.lastResult" class="import-result">{{ importState.lastResult }}</p>
            </div>
            <div class="import-actions">
              <input
                :key="csvInputKey"
                ref="csvInput"
                type="file"
                class="hidden"
                accept=".csv,text/csv"
                @change="onCsvSelected"
              />
              <button type="button" class="btn ghost" :disabled="importState.parsing" @click="triggerCsvPicker">
                {{ importState.parsing ? 'Carico…' : 'Carica CSV' }}
              </button>
              <button
                type="button"
                class="btn primary staff-button-positive"
                :disabled="!importState.preview.length || importState.applying || importState.parsing"
                @click="applyCsvImport"
              >
                {{ importState.applying ? 'Importo…' : 'Applica import' }}
              </button>
              <button
                type="button"
                class="btn ghost staff-button-cancel"
                :disabled="!importState.preview.length || importState.applying || importState.parsing"
                @click="clearImportPreview"
              >
                Annulla anteprima
              </button>
              <button type="button" class="btn ghost staff-button-cancel" :disabled="!lastImportBackup || undoingImport" @click="undoLastImport">
                {{ undoingImport ? 'Ripristino…' : 'Annulla ultima import' }}
              </button>
            </div>
          </div>
          <div v-if="importState.errors.length" class="import-errors">
            <div v-for="(msg, idx) in importState.errors" :key="idx" class="error-state small">
              {{ msg }}
            </div>
          </div>
          <div v-if="importState.preview.length" class="import-preview">
            <div class="import-summary">
              <span>Ordini: {{ importState.stats.orders }}</span>
              <span>Rate: {{ importState.stats.installments }}</span>
              <span>Pagate: {{ importState.stats.paid }}</span>
              <span v-if="importState.fileName">File: {{ importState.fileName }}</span>
            </div>
            <div class="preview-table">
              <div class="preview-row header">
                <div>Ordine</div>
                <div>Email</div>
                <div>Rate</div>
                <div>Pagate</div>
                <div>Prima scadenza</div>
                <div>Ultima scadenza</div>
              </div>
              <div v-for="group in importState.preview" :key="group.orderId" class="preview-row">
                <div class="strong">{{ group.orderId }}</div>
                <div>{{ group.userEmail || '—' }}</div>
                <div>{{ group.manualInstallments.length }}</div>
                <div>
                  {{ group.manualInstallments.filter(i => normalizeStatus(i.status) === 'pagato').length }}
                </div>
                <div class="add-installment-row">
                  <div class="add-installment-title">Aggiungi rata</div>
                  <div class="add-installment-form">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      class="input"
                      v-model.number="newInstallment.amount"
                      placeholder="Importo"
                    />
                    <select class="input inline-select" v-model="newInstallment.method">
                      <option value="">Metodo</option>
                      <option v-for="option in manualInstallmentMethods" :key="option" :value="option">
                        {{ formatLabel(option) }}
                      </option>
                    </select>
                    <select
                      v-if="normalizeMethod(newInstallment.method) === 'alma'"
                      class="input inline-select"
                      v-model.number="newInstallment.almaInstallments"
                    >
                      <option disabled value="">Rate Alma</option>
                      <option v-for="rate in almaInstallmentsOptions" :key="rate" :value="rate">
                        {{ rate }} rate
                      </option>
                    </select>
                    <input
                      type="date"
                      class="input date-input"
                      v-model="newInstallment.scheduledDate"
                    />
                    <button
                      class="btn tiny primary staff-button-positive"
                      :disabled="addingInstallment || !canAddInstallment(group)"
                      @click="addInstallment(group)"
                    >
                      {{ addingInstallment ? 'Aggiungo…' : 'Aggiungi' }}
                    </button>
                  </div>
                </div>
                <div>
                  {{ group.manualInstallments.length ? formatDate(group.manualInstallments[0].scheduledDate) : '—' }}
                </div>
                <div>
                  {{
                    group.manualInstallments.length
                      ? formatDate(group.manualInstallments[group.manualInstallments.length - 1].scheduledDate)
                      : '—'
                  }}
                </div>
              </div>
            </div>
          </div>
        </section>

          <section class="toolbar-card card-surface filters-card">
            <div class="toolbar">
              <div class="toolbar-group toolbar-group--lg">
                <label class="toolbar-label" for="user-state-filter">Filtro utenti</label>
                <select id="user-state-filter" v-model="filters.userState" class="input">
                  <option v-for="option in userFilterOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>
            <div class="toolbar-group toolbar-group--md">
              <label class="toolbar-label" for="stop-filter">STOP</label>
              <select id="stop-filter" v-model="filters.stop" class="input">
                <option value="exclude">Nascondi STOP</option>
                <option value="">Tutte</option>
                <option value="only">Solo STOP</option>
              </select>
            </div>
            <div class="toolbar-group toolbar-group--md">
              <label class="toolbar-label" for="hold-filter">Utenti sospesi</label>
              <select id="hold-filter" v-model="filters.operationalHold" class="input">
                <option value="exclude">Nascondi sospesi</option>
                <option value="">Tutti</option>
                <option value="only">Solo sospesi</option>
              </select>
            </div>
            <div class="toolbar-group toolbar-group--md">
              <label class="toolbar-label" for="studio-filter">Studio</label>
              <select id="studio-filter" v-model="filters.studio" class="input">
                <option value="">Tutti</option>
                <option v-for="studio in studioOptions" :key="studio" :value="studio">
                  {{ studio || '—' }}
                </option>
              </select>
            </div>
            <div class="toolbar-group toolbar-group--md">
              <label class="toolbar-label" for="from-filter">Dal</label>
              <input
                id="from-filter"
                type="date"
                class="input date-input"
                v-model="filters.fromDate"
              />
            </div>
            <div class="toolbar-group toolbar-group--md">
              <label class="toolbar-label" for="to-filter">Al</label>
              <input
                id="to-filter"
                type="date"
                class="input date-input"
                v-model="filters.toDate"
              />
            </div>
            <div class="toolbar-group toolbar-group--lg grow">
              <label class="toolbar-label" for="search-filter">Cerca</label>
              <input
                id="search-filter"
                type="search"
                class="input"
                v-model.trim="filters.search"
                placeholder="Email o ID ordine"
                @keyup.enter="applyFilters"
              />
            </div>
            <div class="toolbar-actions">
              <button type="button" class="btn ghost" @click="resetFilters">Reset</button>
             <button
                type="button"
                class="btn ghost"
                :disabled="loading || fixingFuture"
                @click="normalizeFutureInstallments"
              >
                {{ fixingFuture ? 'Normalizzo…' : 'Normalizza rate future' }}
              </button>
              <button type="button" class="btn primary staff-button-neutral" :disabled="loading" @click="loadInstallments">
                {{ loading ? 'Aggiorno…' : 'Aggiorna' }}
              </button>
            </div>
            </div>
            <div class="filters-note">
              <span v-if="flatInstallments.length">
                Rate visualizzate: {{ stats.total }} / {{ flatInstallments.length }}
                · Scadute {{ stats.expiredInstallments }}
                · In attesa {{ stats.pendingInstallments }}
                · Annullate {{ stats.cancelledInstallments }}
                · Utenti {{ stats.totalUsers }}
                · Utenti con scaduti {{ stats.expiredUsers }}
              </span>
              <span v-else>Nessuna rata manuale trovata.</span>
            <span v-if="lastUpdatedAt" class="muted">Ultimo aggiornamento {{ formatDateTime(lastUpdatedAt) }}</span>
            <span v-if="lastFixResult" class="muted">{{ lastFixResult }}</span>
          </div>
        </section>

        <section class="card-surface" v-if="error">
          <p class="error-state">{{ error }}</p>
        </section>

        <section class="card-surface" v-else-if="loading">
          <p class="muted">Caricamento rate manuali…</p>
        </section>

        <section class="card-surface" v-else>
          <div v-if="!filteredInstallments.length" class="empty-state">
            Nessuna rata trovata con i filtri correnti.
          </div>
          <div v-else class="accordion-list">
            <div
              class="accordion-item"
              v-for="group in userGroups"
              :key="group.key"
              :class="{ 'has-overdue': group.stats.expired > 0, 'has-stop': group.hasStop }"
            >
              <div class="accordion-header">
                <button class="accordion-toggle" type="button" @click="toggleGroup(group.key)">
                  <div class="toggle-cols">
                    <div class="toggle-col">
                      <div class="toggle-title-row">
                        <span class="toggle-title">{{ group.email || 'Email mancante' }}</span>
                        <button
                          v-if="group.email && group.email !== '—'"
                          type="button"
                          class="email-profile-btn"
                          :disabled="isProfileLoading(group)"
                          @click.stop="openUserProfileForGroup(group)"
                          title="Apri profilo utente"
                          aria-label="Apri profilo utente"
                        >
                          <span class="material-icons" aria-hidden="true">person</span>
                        </button>
                      </div>
                      <span class="toggle-subtitle">
                        {{ group.installments.length }} rate ·
                        {{ group.stats.paid }}/{{ group.installments.length }} pagate ·
                        {{ group.stats.expired }} scadute ·
                        {{ group.stats.pending }} in attesa ·
                        {{ group.stats.cancelled }} annullate
                      </span>
                      <div class="toggle-chips">
                        <span v-if="group.stats.settled" class="chip success">Saldato</span>
                        <span v-if="group.hasImported" class="chip info">Importata</span>
                        <span v-if="group.hasStop" class="chip stop">STOP</span>
                        <span v-if="group.hasOperationalHold" class="chip stop">Sospeso</span>
                      </div>
                    </div>
                    <div class="toggle-col toggle-col--meta">
                      <div class="toggle-meta-right">
                        <span v-if="group.productLabel" class="toggle-pill product-pill">
                          {{ group.productLabel }}
                        </span>
                        <span class="toggle-pill amount-pill">
                          Totale rate: {{ formatPrice(group.totalAmount) }}
                        </span>
                      </div>
                    </div>
                    <div class="toggle-col toggle-col--chevron">
                      <span class="material-icons chevron-icon" aria-hidden="true">
                        {{ isGroupOpen(group.key) ? 'expand_less' : 'expand_more' }}
                      </span>
                    </div>
                  </div>
                </button>
                <div class="accordion-actions">
                  <button
                    type="button"
                    class="btn tiny ghost stop-btn"
                    :disabled="isTogglingStop(group.key)"
                    @click.stop="toggleGroupStop(group)"
                  >
                    <span class="material-icons" aria-hidden="true">block</span>
                    <span>{{ group.hasStop ? 'Rimuovi STOP' : 'Segna STOP' }}</span>
                  </button>
                </div>
              </div>

              <div class="accordion-panel" v-show="isGroupOpen(group.key)">
                  <div class="installments-grid">
                    <div class="installment-row header">
                      <div>Rata</div>
                      <div>Importo · Metodo</div>
                      <div>Scadenza</div>
                      <div>Stato rata</div>
                      <div>Note</div>
                      <div class="align-right">Azioni</div>
                    </div>
                      <div
                        v-for="row in group.installments"
                        :key="row.key"
                        class="installment-row"
                        :class="{
                          overdue: isOperationallyExpired(row),
                        }"
                      >
                    <div class="installment-col">
                      <div class="strong">#{{ row.index + 1 }} / {{ row.totalInstallments }}</div>
                      <div class="muted">Ordine {{ row.orderId }}</div>
                    </div>
                    <div class="installment-col">
                      <div class="amount-edit">
                        <input
                          v-if="canEditAmount(row)"
                          type="number"
                          class="input amount-input"
                          min="0"
                          step="0.01"
                          :value="row.amount"
                          :disabled="isSaving(row)"
                          @change="setInstallmentAmount(row, $event.target.value)"
                        />
                        <div v-else class="strong">{{ formatPrice(row.amount) }}</div>
                      </div>
                      <div class="method-picker">
                        <select
                          class="input inline-select"
                          :value="row.method || ''"
                          :disabled="isSaving(row) || isTerminalOrder(row.order) || isInstallmentPaid(row)"
                          @change="handleInstallmentMethodChange(row, $event.target.value)"
                        >
                          <option value="">Metodo pagamento</option>
                          <option v-for="option in manualInstallmentMethods" :key="option" :value="option">
                            {{ formatLabel(option) }}
                          </option>
                        </select>
                        <div v-if="normalizeMethod(row.method) === 'alma'" class="alma-installments-row">
                          <select
                            class="input inline-select"
                            :value="row.almaInstallments || ''"
                            :disabled="isSaving(row) || isTerminalOrder(row.order) || isInstallmentPaid(row)"
                            @change="setInstallmentAlmaInstallments(row, $event.target.value)"
                          >
                            <option value="">Rate Alma</option>
                            <option v-for="rate in almaInstallmentsOptions" :key="rate" :value="rate">
                              {{ rate }} rate
                            </option>
                          </select>
                        </div>
                        <div
                          v-if="isPaymentProofMethod(row.method) || row.paymentProof?.url"
                          class="proof-row"
                        >
                          <input
                            :id="proofInputId(row)"
                            type="file"
                            class="hidden"
                            accept="application/pdf,image/*"
                            @change="onInstallmentProofSelected(row, $event)"
                          />
                          <button
                            type="button"
                            class="btn tiny ghost"
                            :disabled="isSaving(row)"
                            @click="triggerProofPicker(row)"
                          >
                            {{ row.paymentProof?.url ? 'Sostituisci file' : 'Carica file' }}
                          </button>
                          <a
                            v-if="row.paymentProof?.url"
                            :href="row.paymentProof.url"
                            target="_blank"
                            rel="noopener"
                            class="proof-link"
                          >
                            {{ proofDisplayLabel(row.paymentProof) }}
                          </a>
                        </div>
                      </div>
                    </div>
                    <div class="installment-col">
                      <div class="date-edit" v-if="canEditScheduledDate(row)">
                        <input
                          type="date"
                          class="input date-input"
                          :value="row.scheduledDate || ''"
                          :disabled="isSaving(row)"
                          @change="setInstallmentDate(row, $event.target.value)"
                        />
                      </div>
                      <div v-else>
                        {{ row.scheduledDate ? formatDate(row.scheduledDate) : '—' }}
                      </div>
                        <span v-if="isOperationallyExpired(row)" class="chip danger">
                          Scaduta
                        </span>
                    </div>
                    <div class="installment-col">
                      <span class="status-chip" :class="'status-chip--' + statusClass(row.statusNorm)">
                        {{ formatStatus(row.status) }}
                      </span>
                      <div v-if="row.paidAt" class="hint">Pagata il {{ formatDateTime(row.paidAt) }}</div>
                    </div>
                    <div class="installment-col">
                      <input
                        type="text"
                        class="input note-input"
                        :value="row.note || ''"
                        :disabled="isSaving(row)"
                        @change="setInstallmentNote(row, $event.target.value)"
                        placeholder="Aggiungi una nota"
                      />
                    </div>
                    <div class="installment-actions">
                      <button
                        class="icon-btn danger"
                        :disabled="isSaving(row) || isInstallmentPaid(row) || isTerminalOrder(row.order)"
                        @click="removeInstallment(row)"
                        title="Elimina rata"
                        aria-label="Elimina rata"
                      >
                        <span class="material-icons" aria-hidden="true">delete</span>
                      </button>
                      <button
                        class="icon-btn primary"
                        v-if="row.statusNorm !== 'pagato'"
                        :disabled="isSaving(row)"
                        @click="markPaid(row)"
                        title="Segna pagata"
                        aria-label="Segna pagata"
                      >
                        <span class="material-icons" aria-hidden="true">check_circle</span>
                      </button>
                      <button
                        v-if="canCreateAlmaInstallmentLink(row)"
                        class="btn tiny ghost staff-button-positive"
                        :disabled="isSaving(row)"
                        @click="createAlmaInstallmentLink(row)"
                      >
                        Crea link pagamento
                      </button>
                      <div v-if="row.almaPaymentUrl && !isTerminalOrder(row.order)" class="alma-link-actions">
                        <a
                          :href="row.almaPaymentUrl"
                          target="_blank"
                          rel="noopener"
                          class="btn tiny ghost"
                        >
                          Apri Alma
                        </a>
                        <button class="btn tiny ghost" @click="copy(row.almaPaymentUrl)">Copia link</button>
                      </div>
                      <button
                        class="icon-btn ghost"
                        v-if="row.statusNorm !== 'scaduta' && !isTerminalOrder(row.order) && !isInstallmentPaid(row)"
                        :disabled="isSaving(row)"
                        @click="markExpired(row)"
                        title="Segna scaduta"
                        aria-label="Segna scaduta"
                      >
                        <span class="material-icons" aria-hidden="true">schedule</span>
                      </button>
                      <router-link
                        class="icon-btn ghost"
                        :to="{ path: '/order-list-new', query: { searchEmail: row.userEmail } }"
                        title="Apri ordine"
                        aria-label="Apri ordine"
                      >
                        <span class="material-icons" aria-hidden="true">open_in_new</span>
                      </router-link>
                    </div>
                  </div>
                  <div class="add-installment-row">
                    <div class="add-installment-title">Aggiungi rata</div>
                    <div class="add-installment-form">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        class="input"
                        v-model.number="newInstallment.amount"
                        placeholder="Importo"
                      />
                      <select class="input inline-select" v-model="newInstallment.method">
                        <option value="">Metodo</option>
                        <option v-for="option in manualInstallmentMethods" :key="option" :value="option">
                          {{ formatLabel(option) }}
                        </option>
                      </select>
                      <select
                        v-if="normalizeMethod(newInstallment.method) === 'alma'"
                        class="input inline-select"
                        v-model.number="newInstallment.almaInstallments"
                      >
                        <option disabled value="">Rate Alma</option>
                        <option v-for="rate in almaInstallmentsOptions" :key="rate" :value="rate">
                          {{ rate }} rate
                        </option>
                      </select>
                      <input
                        type="date"
                        class="input date-input"
                        v-model="newInstallment.scheduledDate"
                      />
                      <button
                        class="btn tiny primary staff-button-positive"
                        :disabled="addingInstallment || !canAddInstallment(group)"
                        @click="addInstallment(group)"
                      >
                        {{ addingInstallment ? 'Aggiungo…' : 'Aggiungi' }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import SidebarComponent from '../SidebarComponent.vue';
import { getAuth } from 'firebase/auth';
import { getAlmaFunctionUrl, getEnvVar } from '@/lib/env';
import { shouldHideFromOperationalLists } from '@/lib/operationalHold';
import {
  deriveManualInstallmentStatus,
  isManualInstallmentOperationallyExpired,
  isManualInstallmentOperationallyPending,
  isManualInstallmentOverdue,
  isManualInstallmentPaid,
  isManualInstallmentsOrderTerminal,
  isManualInstallmentsTerminalStatus,
  isManualPaymentProofMethod,
  manualInstallmentPaymentProof,
  manualInstallmentProofLabel,
  manualInstallmentStats,
  manualInstallmentsOrderState,
  parseManualInstallmentDueDate,
} from '@/lib/manualInstallments';
import {
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  limit,
  runTransaction,
  updateDoc,
  doc,
  getDoc,
} from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';

const auth = getAuth();
const storage = getStorage();

export default {
  name: 'ManualInstallments',
  components: { SidebarComponent },
  data() {
      return {
        db: null,
        loading: false,
        error: '',
        orders: [],
        lastUpdatedAt: '',
        userFilterOptions: [
          { value: '', label: 'Tutti' },
          { value: 'overdue', label: 'Utenti con rate scadute' },
          { value: 'pending', label: 'Utenti con rate in attesa' },
          { value: 'settled', label: 'Utenti saldati' },
        ],
        manualInstallmentMethods: ['contanti', 'bonifico', 'addebito_diretto', 'pos', 'welfare', 'alma'],
        almaInstallmentsOptions: [2, 3, 4, 6, 10],
        filters: {
          userState: '',
          search: '',
          fromDate: '',
          toDate: '',
          stop: 'exclude',
          operationalHold: 'exclude',
          studio: '',
      },
      importState: {
        parsing: false,
        applying: false,
        preview: [],
        errors: [],
        fileName: '',
        stats: { orders: 0, installments: 0, paid: 0 },
        lastResult: '',
      },
      csvInputKey: 0,
      lastImportBackup: null,
      undoingImport: false,
      saving: {}, // { [installmentKey]: boolean }
      openGroups: {}, // { [emailKey]: boolean }
      fixingFuture: false,
      lastFixResult: '',
      userStudios: {}, // { [uid]: studio }
      fetchingStudioUids: new Set(),
      togglingStop: {}, // { [groupKey]: boolean }
      addingInstallment: false,
      newInstallment: { amount: null, method: '', scheduledDate: '', note: '', almaInstallments: '' },
      userUidByEmail: {}, // { [emailLower]: uid }
      userProfileLoading: {}, // { [groupKey]: boolean }
      userOperationalStateByUid: {},
      userOperationalStateByEmail: {},
    };
  },
  created() {
    this.db = getFirestore();
    this.prefillFiltersFromRoute();
    this.loadInstallments();
  },
  computed: {
    flatInstallments() {
      const rows = [];
      for (const order of this.orders) {
        const plan = Array.isArray(order.manualInstallments) ? order.manualInstallments : [];
        plan.forEach((inst, idx) => {
          const orderId = order.orderId || order._id || '';
          const scheduledDate = inst.scheduledDate || '';
          const statusNorm = this.deriveInstallmentStatus({ ...inst, scheduledDate }, order);
          const dueTs = this.parseDueDate(scheduledDate);
          const overdue = this.isInstallmentOverdue(
            { ...inst, status: statusNorm, scheduledDate },
            order
          );
          rows.push({
            key: `${orderId}:${idx}`,
            order,
            orderId,
            docId: order._id || orderId,
            source: order._source || order.source || '',
            index: idx,
            installmentId: inst.id !== undefined && inst.id !== null ? inst.id : null,
            totalInstallments: plan.length,
            amount: Number(inst.amount) || 0,
            method: inst.method || '',
            scheduledDate,
            dueTs,
            overdue,
            imported: !!inst.imported,
            isStop: !!order.manualInstallmentsStop,
            orderTerminal: this.isTerminalOrder(order),
            status: statusNorm,
            statusNorm,
            paidAt: inst.paidAt || null,
            paymentProof: manualInstallmentPaymentProof(inst, order),
            almaPaymentId: inst.almaPaymentId || null,
            almaPaymentUrl: inst.almaPaymentUrl || null,
            almaPaymentState: inst.almaPaymentState || null,
            almaProcessingStatus: inst.almaProcessingStatus || null,
            almaPaymentCreatedAt: inst.almaPaymentCreatedAt || null,
            almaLastSyncAt: inst.almaLastSyncAt || null,
            almaInstallments: inst.almaInstallments || null,
            note: inst.note || '',
            userEmail: order.userEmail || order.customerEmail || '',
            orderStatus: order.status || '',
            totalPrice: order.totalPrice || null,
            productName: this.getOrderProductName(order),
          });
          });
        }
        return rows;
      },
      filteredInstallments() {
        const searchNeedle = (this.filters.search || '').trim().toLowerCase();
        const userStateFilter = this.normalizeUserStateFilter(this.filters.userState);
        const fromTs = this.parseDueDate(this.filters.fromDate);
        const toTs = this.parseDueDate(this.filters.toDate);
        const hasDateFilter = !!(fromTs || toTs);
        const studioFilter = this.normalizeStudioKey(this.filters.studio);
        const stopFilter = this.filters.stop;
        const holdFilter = this.filters.operationalHold;

        const baseRows = this.flatInstallments.filter(row => {
          if (stopFilter === 'only' && !row.isStop) return false;
          if (stopFilter === 'exclude' && row.isStop) return false;
          const isHeld = this.isOperationallyHidden(row);
          if (holdFilter === 'only' && !isHeld) return false;
          if (holdFilter === 'exclude' && isHeld) return false;
          if (studioFilter) {
            const rowStudio = this.normalizeStudioKey(this.getStudioForRow(row));
            if (rowStudio !== studioFilter) return false;
          }
          if (searchNeedle) {
            const haystack = [
            row.orderId,
            row.userEmail,
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();
            if (!haystack.includes(searchNeedle)) return false;
          }
          return true;
        });

        const groupedRows = new Map();
        baseRows.forEach(row => {
          const key = this.getInstallmentGroupKey(row);
          if (!groupedRows.has(key)) groupedRows.set(key, []);
          groupedRows.get(key).push(row);
        });

        const matchingGroupKeys = new Set();
        groupedRows.forEach((rows, key) => {
          const criteriaRows = hasDateFilter
            ? rows.filter(row => this.rowMatchesDateRange(row, fromTs, toTs))
            : rows;
          if (hasDateFilter && !criteriaRows.length) return;

          const groupStats = this.installmentStats(rows);
          const criteriaStats = this.installmentStats(criteriaRows);
          let matches = true;

          if (userStateFilter === 'overdue') {
            matches = criteriaStats.expired > 0;
          } else if (userStateFilter === 'pending') {
            matches = groupStats.expired === 0 && criteriaStats.pending > 0;
          } else if (userStateFilter === 'settled') {
            matches = groupStats.settled;
          }

          if (matches) matchingGroupKeys.add(key);
        });

        const filtered = baseRows.filter(row => matchingGroupKeys.has(this.getInstallmentGroupKey(row)));

        return this.sortInstallmentRows(filtered);
      },
    studioOptions() {
      const opts = new Map();
      (this.orders || []).forEach(order => {
        const studio = this.normalizeStudio(order.studio || order.studioName);
        const key = this.normalizeStudioKey(studio);
        if (key && !opts.has(key)) opts.set(key, studio);
      });
      Object.values(this.userStudios || {}).forEach(studio => {
        const norm = this.normalizeStudio(studio);
        const key = this.normalizeStudioKey(norm);
        if (key && !opts.has(key)) opts.set(key, norm);
      });
      return Array.from(opts.values()).sort((a, b) => a.localeCompare(b));
    },
      userGroups() {
        const map = new Map();
        this.filteredInstallments.forEach(row => {
          const key = this.getInstallmentGroupKey(row);
          if (!map.has(key)) {
            map.set(key, {
              key,
              email: row.userEmail || '—',
              installments: [],
            });
          }
          map.get(key).installments.push(row);
        });

        const groups = Array.from(map.values()).map(group => {
          const installments = this.sortInstallmentRows(group.installments);
          const groupStats = this.installmentStats(installments);
          const productLabel = (installments.find(r => r.productName) || {}).productName || '';
          const totalAmount = installments.reduce((acc, r) => acc + (Number(r.amount) || 0), 0);
          return {
            ...group,
            installments,
            stats: {
              paid: groupStats.paid,
              expired: groupStats.expired,
              overdue: groupStats.expired,
              pending: groupStats.pending,
              cancelled: groupStats.cancelled,
              settled: groupStats.settled,
            },
            hasImported: installments.some(r => r.imported),
            hasStop: installments.some(r => r.isStop),
            hasOperationalHold: installments.some(r => this.isOperationallyHidden(r)),
            productLabel,
            totalAmount,
          };
        });

        // Ordina gruppi: scaduti prima, poi alfabetico
        return groups.sort((a, b) => {
          if (a.stats.expired !== b.stats.expired) return b.stats.expired - a.stats.expired;
          if (a.stats.pending !== b.stats.pending) return b.stats.pending - a.stats.pending;
          return (a.email || '').localeCompare(b.email || '');
        });
      },
      stats() {
        const total = this.filteredInstallments.length;
        const rowStats = this.installmentStats(this.filteredInstallments);
        const users = new Map();

        this.filteredInstallments.forEach(r => {
          const key = this.getInstallmentGroupKey(r);
          if (!users.has(key)) users.set(key, { total: 0, paid: 0, expired: false });
          const user = users.get(key);
          user.total += 1;
          if (r.statusNorm === 'pagato') user.paid += 1;
          if (this.isOperationallyExpired(r)) user.expired = true;
        });

        const totalUsers = users.size;
        const userValues = Array.from(users.values());
        const expiredUsers = userValues.filter(u => u.expired).length;
        const settledUsers = userValues.filter(u => u.total > 0 && u.paid === u.total).length;
        return {
          total,
          paid: rowStats.paid,
          open: Math.max(total - rowStats.paid - rowStats.cancelled, 0),
          overdue: rowStats.expired,
          expiredInstallments: rowStats.expired,
          pendingInstallments: rowStats.pending,
          cancelledInstallments: rowStats.cancelled,
          totalUsers,
          expiredUsers,
          settledUsers,
        };
      },
    },
    methods: {
      normalizeUserStateFilter(value) {
        const normalized = (value || '').toString().trim().toLowerCase();
        return ['overdue', 'pending', 'settled'].includes(normalized) ? normalized : '';
      },
      getInstallmentGroupKey(row) {
        return (row?.userEmail || 'unknown').toString().trim().toLowerCase() || 'unknown';
      },
      isOperationallyExpired(row) {
        return isManualInstallmentOperationallyExpired(row, Date.now(), row?.order || {});
      },
      isOperationallyPending(row) {
        return isManualInstallmentOperationallyPending(row, Date.now(), row?.order || {});
      },
      isInstallmentPaid(inst) {
        return isManualInstallmentPaid(inst);
      },
      installmentStats(rows = []) {
        return manualInstallmentStats(rows);
      },
      rowMatchesDateRange(row, fromTs, toTs) {
        if ((fromTs || toTs) && !row?.dueTs) return false;
        if (fromTs && row.dueTs < fromTs) return false;
        if (toTs && row.dueTs > toTs) return false;
        return true;
      },
      sortInstallmentRows(rows = []) {
        return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
          if (a.dueTs && b.dueTs) return a.dueTs - b.dueTs;
          if (a.dueTs) return -1;
          if (b.dueTs) return 1;
          if (a.orderId !== b.orderId) return String(a.orderId || '').localeCompare(String(b.orderId || ''));
          return (Number(a.index) || 0) - (Number(b.index) || 0);
        });
      },
      formatLabel(value) {
        if (!value) return '—';
        return value
        .toString()
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },
    formatStatus(status) {
      if (this.normalizeStatus(status) === 'annullata') return 'Annullata / non dovuta';
      return this.formatLabel(status || '—');
    },
    statusClass(status) {
      return (status || 'unknown')
        .toString()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-');
    },
    formatPrice(v) {
      if (v === null || v === undefined || v === '') return '—';
      const n = Number(v);
      if (!Number.isFinite(n)) return '—';
      return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
    },
    formatDate(value) {
      if (!value) return '—';
      const d = new Date(String(value).replace(' ', 'T'));
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleDateString('it-IT');
    },
    formatDateTime(value) {
      if (!value) return '';
      const d = new Date(String(value).replace(' ', 'T'));
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString('it-IT');
    },
    normalizeStatus(value) {
      const normalized = (value || '').toString().trim().toLowerCase();
      if (normalized === 'scaduto') return 'scaduta';
      return normalized;
    },
    normalizeEmail(value) {
      return (value || '').toString().trim().toLowerCase();
    },
    normalizeMethod(value) {
      return (value || '').toString().trim().toLowerCase();
    },
    isPaymentProofMethod(value) {
      return isManualPaymentProofMethod(value);
    },
    isAlmaInstallment(row) {
      return this.normalizeMethod(row?.method) === 'alma';
    },
    canCreateAlmaInstallmentLink(row) {
      if (!row) return false;
      if (this.isTerminalOrder(row.order)) return false;
      if (!this.isAlmaInstallment(row)) return false;
      if (this.normalizeStatus(row.statusNorm || row.status) === 'pagato' || row.paidAt) return false;
      if (row.almaPaymentUrl || row.almaPaymentId) return false;
      const amount = Number(row.amount);
      if (!Number.isFinite(amount) || amount <= 0) return false;
      const almaCount = Number(row.almaInstallments);
      return Number.isFinite(almaCount) && almaCount > 0;
    },
    isBlockedOrderStatus(statusRaw) {
      const status = String(statusRaw || '').trim().toLowerCase();
      if (!status) return false;
      return (
        isManualInstallmentsTerminalStatus(statusRaw) ||
        status.includes('rimborsato') ||
        status.includes('refunded') ||
        status.includes('failed')
      );
    },
    isTerminalOrder(order = {}) {
      return isManualInstallmentsOrderTerminal(order);
    },
    proofDisplayLabel(proof = {}) {
      return manualInstallmentProofLabel(proof);
    },
    isValidProofFile(file) {
      if (!file) return false;
      const type = (file.type || '').toLowerCase();
      const name = (file.name || '').toLowerCase();
      if (type === 'application/pdf') return true;
      if (type.startsWith('image/')) return true;
      const allowedExt = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp', '.heic', '.heif'];
      return allowedExt.some(ext => name.endsWith(ext));
    },
    proofInputId(row) {
      const raw = row?.key ? String(row.key) : '';
      const safe = raw.replace(/[^a-z0-9_-]/gi, '_');
      return `proof-input-${safe || 'row'}`;
    },
    triggerProofPicker(row) {
      const id = this.proofInputId(row);
      const el = id ? document.getElementById(id) : null;
      if (el && el.click) el.click();
    },
    async copy(txt) {
      if (!txt) return;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(txt);
          alert('Link copiato negli appunti');
          return;
        }
      } catch (e) {
        // fallback
      }
      try {
        const ta = document.createElement('textarea');
        ta.value = txt;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        alert('Link copiato (fallback)');
        return;
      } catch (e) {
        // last fallback
      }
      prompt('Copia manualmente il link:', txt);
    },
    async callAlmaFunction(type, payload = {}) {
      const url = getAlmaFunctionUrl(type);
      if (!url) throw new Error(`Endpoint Alma non configurato per ${type}`);

      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const text = await resp.text();
      if (!resp.ok) {
        let detail = text || resp.statusText;
        try {
          const parsed = text ? JSON.parse(text) : null;
          if (parsed?.error) detail = parsed.error;
          else if (parsed?.message) detail = parsed.message;
          else if (parsed) detail = JSON.stringify(parsed);
        } catch (e) {
          // ignore
        }
        throw new Error(detail || 'Errore richiesta Alma');
      }
      if (!text) return {};
      try {
        return JSON.parse(text);
      } catch (err) {
        console.warn('Risposta Alma non JSON:', err);
        return {};
      }
    },
    buildAlmaInstallmentPayload(order, inst, rowIndex) {
      if (!order || !inst) return null;
      const docId = String(order._id || order.orderId || '');
      if (!docId) return null;
      const amount = Number(inst.amount) || 0;
      if (!Number.isFinite(amount) || amount <= 0) return null;
      const almaCount = Number(inst.almaInstallments);
      if (!Number.isFinite(almaCount) || almaCount <= 0) return null;

      const basePath = (process.env.BASE_URL || '/').replace(/\/?$/, '/');
      const appBaseUrl = window.location.origin + basePath;
      const configuredReturn = (getEnvVar('VITE_ALMA_RETURN_URL') || '').trim();
      const configuredCancel = (getEnvVar('VITE_ALMA_CANCEL_URL') || '').trim();
      const configuredFailure = (getEnvVar('VITE_ALMA_FAILURE_URL') || '').trim();
      const configuredCustomerUrl = (getEnvVar('VITE_ALMA_CUSTOMER_URL') || '').trim();
      const configuredMerchantUrl = (getEnvVar('VITE_ALMA_MERCHANT_URL') || '').trim();
      const captureMethodEnv = (getEnvVar('VITE_ALMA_CAPTURE_METHOD') || '').trim().toLowerCase();
      const expiresAfterEnv = parseInt((getEnvVar('VITE_ALMA_EXPIRES_AFTER_MINUTES') || '').trim(), 10);
      const locale = (getEnvVar('VITE_ALMA_LOCALE') || 'it').trim() || 'it';
      const ipnUrl = (getEnvVar('VITE_ALMA_IPN_URL') || getEnvVar('VITE_ALMA_WEBHOOK_URL') || '').trim();

      const almaReturnUrl = configuredReturn || `${appBaseUrl}#/orders-general`;
      const almaCancelUrl = configuredCancel || almaReturnUrl;
      const almaFailureUrl = configuredFailure || almaCancelUrl;

      const customer = {
        email: order.userEmail || order.customerEmail || '',
        firstName: order.customerName || order.customerFirstName || order.userName || '',
        lastName: order.customerSurname || order.customerLastName || order.userSurname || '',
        phone: order.customerPhone || order.phone || order.userPhone || '',
        studio: order.customerStudio || order.userStudio || order.studio || '',
      };

      const payload = {
        orderId: docId,
        amount: amount.toFixed(2),
        installments: almaCount,
        userEmail: customer.email || null,
        customer: {
          firstName: (customer.firstName || '').trim(),
          lastName: (customer.lastName || '').trim(),
          phone: (customer.phone || '').trim(),
          studio: (customer.studio || '').trim(),
          isBusiness: false,
        },
        returnUrl: almaReturnUrl,
        cancelUrl: almaCancelUrl,
        failureUrl: almaFailureUrl,
        customerUrl: configuredCustomerUrl || almaReturnUrl,
        merchantUrl: configuredMerchantUrl || '',
        locale,
        metadata: {
          createdFrom: 'ManualInstallments',
          manualInstallment: true,
          installmentIndex: rowIndex + 1,
          installmentId: inst.id !== undefined && inst.id !== null ? inst.id : null,
        },
      };
      if (ipnUrl) payload.ipnCallbackUrl = ipnUrl;
      if (captureMethodEnv === 'manual' || captureMethodEnv === 'automatic') {
        payload.captureMethod = captureMethodEnv;
      }
      if (Number.isFinite(expiresAfterEnv) && expiresAfterEnv > 0) {
        payload.expiresAfter = expiresAfterEnv;
      }
      return payload;
    },
    async createAlmaInstallmentLink(row) {
      if (!row) return;
      const order = row.order || this.getOrderById(row.docId);
      if (!order) {
        alert('Ordine non trovato.');
        return;
      }
      if (this.isTerminalOrder(order)) {
        alert('Non puoi generare un nuovo link di pagamento per un ordine annullato.');
        return;
      }
      if (!this.canCreateAlmaInstallmentLink(row)) return;

      const idx = Number(row.index);
      if (!Number.isInteger(idx) || idx < 0) return;

      const plan = Array.isArray(order.manualInstallments) ? order.manualInstallments.map(inst => ({ ...inst })) : [];
      const target = plan[idx];
      if (!target) return;

      const payload = this.buildAlmaInstallmentPayload(order, target, idx);
      if (!payload) {
        alert('Dati insufficienti per generare il link Alma.');
        return;
      }

      this.setSaving(row.key, true);
      try {
        await this.callAlmaFunction('create', payload);
        // La Cloud Function è la writer autorevole del link nella rata.
        // Ricarichiamo il documento invece di riscrivere un piano potenzialmente stale.
        await this.loadInstallments();
      } catch (err) {
        console.error('createAlmaInstallmentLink error:', err);
        alert(err?.message || 'Errore durante la creazione del link Alma.');
      } finally {
        this.setSaving(row.key, false);
      }
    },
    canEditScheduledDate(row) {
      if (!row) return false;
      const status = this.normalizeStatus(row.statusNorm || row.status);
      return status === 'in attesa di pagamento' || status === 'scaduta';
    },
    canEditAmount(row) {
      if (!row) return false;
      if (this.isTerminalOrder(row.order)) return false;
      const status = this.normalizeStatus(row.statusNorm || row.status);
      return status !== 'pagato';
    },
    normalizeStudio(value) {
      return (value || '').toString().trim();
    },
    normalizeStudioKey(value) {
      if (!value) return '';
      return value
        .toString()
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\s+/g, ' ');
    },
    isFutureDueDate(dateStr) {
      const ts = this.parseDueDate(dateStr);
      return !!ts && ts > Date.now();
    },
    deriveInstallmentStatus(inst, order = {}) {
      return deriveManualInstallmentStatus(inst, Date.now(), order);
    },
    manualInstallmentPaidCount(plan = []) {
      if (!Array.isArray(plan)) return 0;
      return plan.reduce((count, inst) => count + (this.deriveInstallmentStatus(inst) === 'pagato' ? 1 : 0), 0);
    },
    manualInstallmentsOrderState(plan = []) {
      return manualInstallmentsOrderState(plan);
    },
    parseDueDate(dateStr) {
      return parseManualInstallmentDueDate(dateStr);
    },
    isInstallmentOverdue(inst, order = {}) {
      return isManualInstallmentOverdue(inst, Date.now(), order);
    },
    getOrderById(id) {
      if (!id) return null;
      return this.orders.find(o => (o._id || o.orderId) === id) || null;
    },
    resolveInstallmentIndex(plan = [], row = {}) {
      if (!Array.isArray(plan) || !row) return -1;
      const rowId = row.installmentId !== undefined && row.installmentId !== null
        ? String(row.installmentId)
        : '';
      if (rowId) {
        return plan.findIndex(inst => (
          inst?.id !== undefined
          && inst?.id !== null
          && String(inst.id) === rowId
        ));
      }
      const fallbackIndex = Number(row.index);
      return Number.isInteger(fallbackIndex) && fallbackIndex >= 0 && fallbackIndex < plan.length
        ? fallbackIndex
        : -1;
    },
    manualInstallmentGuardError(message) {
      const error = new Error(message);
      error.code = 'manual-installment/guard';
      return error;
    },
    syncCommittedOrder(order, currentOrder = {}, updates = {}) {
      if (!order) return;
      Object.assign(order, currentOrder, updates);
    },
    async mutateInstallmentFresh(row, {
      allowPaid = false,
      allowTerminal = false,
      paidMessage = 'Una rata già pagata non può essere modificata.',
      terminalMessage = 'Le rate di un ordine annullato non possono essere modificate.',
      mutate,
      buildOrderUpdates = null,
    } = {}) {
      if (!row || typeof mutate !== 'function') {
        throw this.manualInstallmentGuardError('Rata non valida. Aggiorna la pagina.');
      }
      const order = row.order || this.getOrderById(row.docId);
      if (!order) throw this.manualInstallmentGuardError('Ordine non trovato.');
      const docId = String(row.docId || order._id || order.orderId || '');
      if (!docId) throw this.manualInstallmentGuardError('Ordine non trovato.');

      let committedOrder = null;
      let committedUpdates = null;
      const orderRef = doc(this.db, 'orders', docId);
      await runTransaction(this.db, async transaction => {
        const orderSnap = await transaction.get(orderRef);
        if (!orderSnap.exists()) {
          throw this.manualInstallmentGuardError('Ordine non trovato.');
        }

        const currentOrder = {
          _id: orderSnap.id,
          ...(orderSnap.data() || {}),
        };
        const plan = Array.isArray(currentOrder.manualInstallments)
          ? currentOrder.manualInstallments.map(inst => ({ ...inst }))
          : [];
        const idx = this.resolveInstallmentIndex(plan, row);
        const target = idx >= 0 ? plan[idx] : null;
        if (!target) {
          throw this.manualInstallmentGuardError('La rata non esiste più. Aggiorna la pagina.');
        }
        if (!allowPaid && this.isInstallmentPaid(target)) {
          throw this.manualInstallmentGuardError(paidMessage);
        }
        if (!allowTerminal && this.isTerminalOrder(currentOrder)) {
          throw this.manualInstallmentGuardError(terminalMessage);
        }

        const nowIso = new Date().toISOString();
        const nextTarget = mutate(
          { ...target },
          { currentOrder, plan, idx, nowIso }
        );
        if (!nextTarget || typeof nextTarget !== 'object') {
          throw this.manualInstallmentGuardError('Modifica rata non valida. Aggiorna la pagina.');
        }
        plan[idx] = nextTarget;

        const extraUpdates = typeof buildOrderUpdates === 'function'
          ? buildOrderUpdates({ currentOrder, plan, idx, target: nextTarget, nowIso })
          : {};
        const updates = {
          ...(extraUpdates || {}),
          manualInstallments: plan,
          updatedAt: nowIso,
        };
        transaction.update(orderRef, updates);
        committedOrder = currentOrder;
        committedUpdates = updates;
      });

      this.syncCommittedOrder(order, committedOrder, committedUpdates);
      return true;
    },
    getOrderProductName(order) {
      if (!order) return '';
      const products = Array.isArray(order.products) ? order.products : [];
      const names = products
        .map(p => p?.name || p?.title || p?.productName || p?.product_title || p?.id)
        .filter(Boolean);
      if (names.length) return names.join(', ');
      return order.productName || order.product || order.title || '';
    },
    canAddInstallment(group) {
      if (!group || !Array.isArray(group.installments)) return false;
      const targetOrder = group.installments[0]?.order || null;
      if (!targetOrder || this.isTerminalOrder(targetOrder)) return false;
      const amt = Number(this.newInstallment.amount);
      const date = (this.newInstallment.scheduledDate || '').trim();
      if (!Number.isFinite(amt) || amt <= 0 || !date) return false;
      const method = this.normalizeMethod(this.newInstallment.method);
      if (method === 'alma') {
        const almaCount = Number(this.newInstallment.almaInstallments);
        if (!Number.isFinite(almaCount) || almaCount <= 0) return false;
      }
      return true;
    },
    getOrderUserUid(order) {
      if (!order) return null;
      return order.userUid || order.userId || order.uid || order.user_uid || null;
    },
    getStudioForRow(row) {
      if (!row) return '';
      const order = row.order || this.getOrderById(row.docId) || {};
      const uid = this.getOrderUserUid(order);
      if (uid && Object.prototype.hasOwnProperty.call(this.userStudios, uid)) {
        const studio = this.normalizeStudio(this.userStudios[uid]);
        if (studio) return studio;
      }
      return order.studio || order.studioName || '';
    },
    getOperationalHoldForRow(row) {
      if (!row) return {};
      const order = row.order || this.getOrderById(row.docId) || {};
      const uid = this.getOrderUserUid(order);
      if (uid && this.userOperationalStateByUid[uid]) {
        return this.userOperationalStateByUid[uid];
      }
      const email = this.normalizeEmail(row.userEmail || order.userEmail || order.customerEmail);
      if (email && this.userOperationalStateByEmail[email]) {
        return this.userOperationalStateByEmail[email];
      }
      return {};
    },
    isOperationallyHidden(row) {
      return shouldHideFromOperationalLists(this.getOperationalHoldForRow(row));
    },
    async findOrderDoc(orderIdStr) {
      const raw = String(orderIdStr || '').trim();
      if (!raw) return null;
      const asNumber = Number(raw);
      const hasNumber = Number.isFinite(asNumber);

      const inMemory = this.getOrderById(raw);
      if (inMemory) {
        return { docId: inMemory._id || inMemory.orderId || raw, order: inMemory, data: inMemory };
      }

      // Provo docId diretto (stringa)
      try {
        const directSnap = await getDoc(doc(this.db, 'orders', raw));
        if (directSnap.exists()) {
          return { docId: raw, order: null, data: directSnap.data() || {} };
        }
      } catch (err) {
        console.error('findOrderDoc direct error:', err);
      }

      // Provo docId numerico (se fornito come "125052" ma docId è numero->string)
      if (hasNumber) {
        const numericId = String(asNumber);
        if (numericId !== raw) {
          try {
            const numericSnap = await getDoc(doc(this.db, 'orders', numericId));
            if (numericSnap.exists()) {
              return { docId: numericId, order: null, data: numericSnap.data() || {} };
            }
          } catch (err) {
            console.error('findOrderDoc numeric direct error:', err);
          }
        }
      }

      // Fallback query su campo orderId (stringa)
      try {
        const q = query(collection(this.db, 'orders'), where('orderId', '==', raw));
        const snap = await getDocs(q);
        const first = snap.docs?.[0];
        if (first) {
          return { docId: first.id, order: null, data: first.data() || {} };
        }
      } catch (err) {
        console.error('findOrderDoc query error (string):', err);
      }

      // Fallback query su campo orderId (numero)
      if (hasNumber) {
        try {
          const qNum = query(collection(this.db, 'orders'), where('orderId', '==', asNumber));
          const snapNum = await getDocs(qNum);
          const firstNum = snapNum.docs?.[0];
          if (firstNum) {
            return { docId: firstNum.id, order: null, data: firstNum.data() || {} };
          }
        } catch (err) {
          console.error('findOrderDoc query error (number):', err);
        }
      }
      return null;
    },
    setSaving(key, value) {
      if (!key) return;
      if (value) {
        if (this.$set) this.$set(this.saving, key, true);
        else this.saving[key] = true;
      } else if (this.saving[key]) {
        if (this.$delete) this.$delete(this.saving, key);
        else delete this.saving[key];
      }
    },
    isSaving(row) {
      if (!row) return false;
      return !!this.saving[row.key];
    },
    profileKey(group) {
      const raw = group?.key || group?.email || '';
      return this.normalizeEmail(raw);
    },
    setProfileLoading(groupOrKey, value) {
      const key = typeof groupOrKey === 'string' ? groupOrKey : this.profileKey(groupOrKey);
      if (!key) return;
      if (value) {
        if (this.$set) this.$set(this.userProfileLoading, key, true);
        else this.userProfileLoading[key] = true;
      } else if (this.userProfileLoading[key]) {
        if (this.$delete) this.$delete(this.userProfileLoading, key);
        else delete this.userProfileLoading[key];
      }
    },
    isProfileLoading(group) {
      const key = this.profileKey(group);
      if (!key) return false;
      return !!this.userProfileLoading[key];
    },
    async resolveUserUidForGroup(group) {
      if (!group) return null;
      const firstRow = Array.isArray(group.installments) ? group.installments[0] : null;
      const order = firstRow?.order || this.getOrderById(firstRow?.docId) || null;
      const direct = this.getOrderUserUid(order);
      if (direct) return direct;

      const emailRaw = (group.email || firstRow?.userEmail || '').trim();
      const normalized = this.normalizeEmail(emailRaw);
      if (normalized && this.userUidByEmail[normalized]) {
        return this.userUidByEmail[normalized];
      }

      const attempts = [];
      if (emailRaw) attempts.push(emailRaw);
      if (normalized && normalized !== emailRaw) attempts.push(normalized);
      if (!attempts.length) return null;

      for (const candidate of attempts) {
        try {
          const snap = await getDocs(
            query(collection(this.db, 'users'), where('email', '==', candidate), limit(1))
          );
          const found = snap.docs?.[0];
          if (found) {
            const uid = found.id;
            if (normalized) {
              if (this.$set) this.$set(this.userUidByEmail, normalized, uid);
              else this.userUidByEmail[normalized] = uid;
            }
            return uid;
          }
        } catch (err) {
          console.error('resolveUserUidForGroup error:', err);
        }
      }
      return null;
    },
    async openUserProfileForGroup(group) {
      if (!group) return;
      const key = this.profileKey(group);
      this.setProfileLoading(key, true);
      try {
        const uid = await this.resolveUserUidForGroup(group);
        if (uid) {
          this.$router.push({ name: 'UserDetails', params: { userId: String(uid) } });
        } else {
          alert('Profilo utente non trovato.');
        }
      } catch (err) {
        console.error('openUserProfileForGroup error:', err);
        alert('Impossibile aprire il profilo utente.');
      } finally {
        this.setProfileLoading(key, false);
      }
    },
    setTogglingStop(key, value) {
      if (!key) return;
      if (value) {
        if (this.$set) this.$set(this.togglingStop, key, true);
        else this.togglingStop[key] = true;
      } else if (this.togglingStop[key]) {
        if (this.$delete) this.$delete(this.togglingStop, key);
        else delete this.togglingStop[key];
      }
    },
    isTogglingStop(key) {
      if (!key) return false;
      return !!this.togglingStop[key];
    },
    async fetchStudiosForOrders(ordersList = []) {
      if (!this.db) return;
      const pending = [];
      ordersList.forEach(order => {
        const uid = this.getOrderUserUid(order);
        if (!uid) return;
        if (Object.prototype.hasOwnProperty.call(this.userStudios, uid)) return;
        if (this.fetchingStudioUids.has(uid)) return;
        this.fetchingStudioUids.add(uid);
        pending.push(uid);
      });

      if (!pending.length) return;
      const chunks = [];
      for (let i = 0; i < pending.length; i += 10) {
        chunks.push(pending.slice(i, i + 10));
      }

      for (const chunk of chunks) {
        const updates = {};
        await Promise.all(
          chunk.map(async uid => {
            try {
              const snap = await getDoc(doc(this.db, 'users', uid));
              const data = snap.exists() ? snap.data() || {} : {};
              updates[uid] = this.normalizeStudio(data.studio || data.studioName || '');
            } catch (err) {
              console.error('fetchStudiosForOrders error:', err);
              updates[uid] = '';
            } finally {
              this.fetchingStudioUids.delete(uid);
            }
          })
        );
        const next = { ...this.userStudios };
        Object.entries(updates).forEach(([uid, studio]) => {
          next[uid] = studio;
        });
        this.userStudios = next;
      }
    },
    async fetchOperationalHoldForOrders(ordersList = []) {
      if (!this.db) return;
      const uidSet = new Set();
      const emailSet = new Set();

      ordersList.forEach(order => {
        const uid = this.getOrderUserUid(order);
        if (uid && !this.userOperationalStateByUid[uid]) {
          uidSet.add(uid);
        }
        const email = this.normalizeEmail(order.userEmail || order.customerEmail || order.email);
        if (email && !this.userOperationalStateByEmail[email]) {
          emailSet.add(email);
        }
      });

      const uidUpdates = {};
      await Promise.all(Array.from(uidSet).map(async uid => {
        try {
          const snap = await getDoc(doc(this.db, 'users', uid));
          const data = snap.exists() ? { uid, ...(snap.data() || {}) } : { uid };
          uidUpdates[uid] = data;
          const email = this.normalizeEmail(data.email);
          if (email) this.userOperationalStateByEmail[email] = data;
        } catch (err) {
          console.error('fetchOperationalHoldForOrders uid error:', err);
          uidUpdates[uid] = { uid };
        }
      }));

      const emailUpdates = {};
      const emails = Array.from(emailSet).filter(email => !this.userOperationalStateByEmail[email]);
      for (let i = 0; i < emails.length; i += 10) {
        const chunk = emails.slice(i, i + 10);
        try {
          // eslint-disable-next-line no-await-in-loop
          const snap = await getDocs(query(collection(this.db, 'users'), where('email', 'in', chunk)));
          snap.forEach(docSnap => {
            const data = { uid: docSnap.id, ...(docSnap.data() || {}) };
            const email = this.normalizeEmail(data.email);
            if (email) emailUpdates[email] = data;
            if (docSnap.id) uidUpdates[docSnap.id] = data;
          });
        } catch (err) {
          console.error('fetchOperationalHoldForOrders email error:', err);
        }
      }

      emails.forEach(email => {
        if (!emailUpdates[email]) emailUpdates[email] = { email };
      });

      this.userOperationalStateByUid = {
        ...this.userOperationalStateByUid,
        ...uidUpdates,
      };
      this.userOperationalStateByEmail = {
        ...this.userOperationalStateByEmail,
        ...emailUpdates,
      };
    },
    async loadInstallments() {
      this.loading = true;
      this.error = '';
      try {
        const q = query(collection(this.db, 'orders'), where('paymentMethod', '==', 'rate_manuali'));
        const snap = await getDocs(q);
        const results = [];
        snap.forEach(docSnap => {
          const data = docSnap.data() || {};
          if (Array.isArray(data.manualInstallments) && data.manualInstallments.length) {
            results.push({
              _id: docSnap.id,
              ...data,
              orderId: data.orderId || docSnap.id,
              _source: data.source || data._source || '',
              manualInstallmentsStop: !!data.manualInstallmentsStop,
            });
          }
        });
        this.orders = results;
        this.fetchStudiosForOrders(results);
        this.fetchOperationalHoldForOrders(results);
        this.lastUpdatedAt = new Date().toISOString();
      } catch (err) {
        console.error('loadInstallments error:', err);
        this.error = 'Errore nel caricamento delle rate manuali.';
      } finally {
        this.loading = false;
      }
    },
    async setInstallmentStatus(row, nextStatus) {
      if (!row) return;
      const order = row.order || this.getOrderById(row.docId);
      if (!order) {
        alert('Ordine non trovato per questa rata.');
        return;
      }
      const docId = String(row.docId || order._id || order.orderId || '');
      if (!docId) return;
      const key = row.key;
      const normalizedNext = this.normalizeStatus(nextStatus);

      this.setSaving(key, true);
      try {
        let committedOrder = null;
        let committedUpdates = null;
        const orderRef = doc(this.db, 'orders', docId);
        await runTransaction(this.db, async transaction => {
          const orderSnap = await transaction.get(orderRef);
          if (!orderSnap.exists()) {
            throw this.manualInstallmentGuardError('Ordine non trovato per questa rata.');
          }

          const currentOrder = {
            _id: orderSnap.id,
            ...(orderSnap.data() || {}),
          };
          const plan = Array.isArray(currentOrder.manualInstallments)
            ? currentOrder.manualInstallments.map(inst => ({ ...inst }))
            : [];
          const idx = this.resolveInstallmentIndex(plan, row);
          const target = idx >= 0 ? plan[idx] : null;
          if (!target) {
            throw this.manualInstallmentGuardError('La rata non esiste più. Aggiorna la pagina.');
          }
          if (this.isInstallmentPaid(target) && normalizedNext !== 'pagato') {
            throw this.manualInstallmentGuardError('Una rata già pagata non può essere declassata.');
          }
          if (this.isTerminalOrder(currentOrder) && normalizedNext !== 'pagato') {
            throw this.manualInstallmentGuardError(
              'Le rate non pagate di un ordine annullato non sono dovute e non possono essere segnate come scadute.'
            );
          }
          if (
            normalizedNext === 'pagato'
            && this.isPaymentProofMethod(target.method)
            && !manualInstallmentPaymentProof(target, currentOrder)?.url
          ) {
            throw this.manualInstallmentGuardError(
              'Per segnare pagata questa rata devi prima caricare l’allegato.'
            );
          }

          const nowIso = new Date().toISOString();
          const existingPaidAt = target.paidAt || target.paid_at || null;
          plan[idx] = {
            ...target,
            status: nextStatus,
            paidAt: normalizedNext === 'pagato' ? (existingPaidAt || nowIso) : null,
          };

          const blocked = (
            this.isTerminalOrder(currentOrder)
            || this.isBlockedOrderStatus(currentOrder.status)
            || this.isBlockedOrderStatus(currentOrder.paymentStatus)
          );
          const state = this.manualInstallmentsOrderState(plan);
          const updates = {
            manualInstallments: plan,
            updatedAt: nowIso,
          };
          if (!blocked) {
            updates.status = state.status;
            updates.paymentStatus = state.paymentStatus;
          }

          transaction.update(orderRef, updates);
          committedOrder = currentOrder;
          committedUpdates = updates;
        });

        this.syncCommittedOrder(order, committedOrder, committedUpdates);
      } catch (err) {
        console.error('setInstallmentStatus error:', err);
        alert(
          err?.code === 'manual-installment/guard'
            ? err.message
            : 'Errore durante l’aggiornamento della rata.'
        );
      } finally {
        this.setSaving(key, false);
      }
    },
    markPaid(row) {
      return this.setInstallmentStatus(row, 'pagato');
    },
    markExpired(row) {
      return this.setInstallmentStatus(row, 'scaduta');
    },
      prefillFiltersFromRoute() {
        if (!this.$route || !this.$route.query) return;
        const { searchEmail, search, due, status, userState, fromDate, toDate, studio, stop } = this.$route.query;
        const nextFilters = { ...this.filters };
        const searchValue = searchEmail || search;
        if (searchValue) nextFilters.search = String(searchValue);
        const routeUserState = this.normalizeUserStateFilter(userState);
        if (routeUserState) {
          nextFilters.userState = routeUserState;
        } else {
          const legacyStatus = this.normalizeStatus(status);
          if (due === 'overdue' || legacyStatus === 'scaduta') {
            nextFilters.userState = 'overdue';
          } else if (due === 'upcoming' || legacyStatus === 'in attesa di pagamento') {
            nextFilters.userState = 'pending';
          } else if (legacyStatus === 'pagato') {
            nextFilters.userState = 'settled';
          }
        }
        if (fromDate) nextFilters.fromDate = String(fromDate);
        if (toDate) nextFilters.toDate = String(toDate);
      if (studio) nextFilters.studio = String(studio);
      if (stop && ['only', 'exclude'].includes(String(stop))) {
        nextFilters.stop = String(stop);
      }
      this.filters = nextFilters;
    },
    async handleInstallmentMethodChange(row, nextMethod) {
      const normalized = this.normalizeMethod(nextMethod);
      const changed = await this.setInstallmentMethod(row, nextMethod);
      if (changed && this.isPaymentProofMethod(normalized)) {
        this.$nextTick(() => this.triggerProofPicker(row));
      }
    },
    async setInstallmentMethod(row, nextMethod) {
      if (!row) return false;
      const normalized = (nextMethod || '').trim();
      const key = row.key;
      this.setSaving(key, true);
      try {
        await this.mutateInstallmentFresh(row, {
          paidMessage: 'Il metodo di pagamento non è modificabile su una rata già pagata.',
          terminalMessage: 'Il metodo di pagamento non è modificabile su un ordine annullato.',
          mutate: target => ({
            ...target,
            method: normalized || null,
          }),
          buildOrderUpdates: ({ plan }) => (
            plan.some(inst => this.normalizeMethod(inst.method) === 'alma')
              ? { paymentProvider: 'alma' }
              : {}
          ),
        });
        return true;
      } catch (err) {
        console.error('setInstallmentMethod error:', err);
        alert(
          err?.code === 'manual-installment/guard'
            ? err.message
            : 'Errore durante l’aggiornamento del metodo di pagamento.'
        );
        return false;
      } finally {
        this.setSaving(key, false);
      }
    },
    async setInstallmentAlmaInstallments(row, nextValue) {
      if (!row) return;
      const almaCount = Number(nextValue);
      if (!Number.isFinite(almaCount) || almaCount <= 0) {
        alert('Seleziona un numero di rate Alma valido.');
        return;
      }
      if (!this.almaInstallmentsOptions.includes(almaCount)) {
        alert('Numero di rate Alma non valido.');
        return;
      }

      const key = row.key;
      this.setSaving(key, true);
      try {
        await this.mutateInstallmentFresh(row, {
          paidMessage: 'Il numero di rate Alma non è modificabile su una rata già pagata.',
          terminalMessage: 'Il numero di rate Alma non è modificabile su un ordine annullato.',
          mutate: target => {
            if (this.normalizeMethod(target.method) !== 'alma') {
              throw this.manualInstallmentGuardError('La rata non usa più Alma. Aggiorna la pagina.');
            }
            return {
              ...target,
              almaInstallments: almaCount,
            };
          },
        });
      } catch (err) {
        console.error('setInstallmentAlmaInstallments error:', err);
        alert(
          err?.code === 'manual-installment/guard'
            ? err.message
            : 'Errore durante l’aggiornamento delle rate Alma.'
        );
      } finally {
        this.setSaving(key, false);
      }
    },
    async onInstallmentProofSelected(row, event) {
      const input = event?.target;
      const file = input?.files?.[0] || null;
      if (!file) return;
      if (!this.isValidProofFile(file)) {
        if (input) input.value = '';
        alert('Carica un PDF o un’immagine valida.');
        return;
      }
      try {
        await this.applyInstallmentProof(row, file);
      } catch (err) {
        console.error('applyInstallmentProof error:', err);
        alert(
          err?.code === 'manual-installment/guard'
            ? err.message
            : 'Errore durante il caricamento del documento.'
        );
      } finally {
        if (input) input.value = '';
      }
    },
    async uploadInstallmentProof(row, file) {
      if (!row || !file) return null;
      const docId = String(row.docId || '');
      if (!docId) throw new Error('ID ordine non valido per il caricamento del documento.');
      const idx = Number.isInteger(Number(row.index)) ? Number(row.index) + 1 : 'x';
      const rawNameExt = (file.name || '').split('.').pop() || '';
      const typeExt = (file.type || '').includes('/') ? file.type.split('/')[1] : '';
      const normalizedExt = rawNameExt || typeExt || 'file';
      const safeExt = normalizedExt.toLowerCase().replace(/[^a-z0-9]/g, '') || 'file';
      const path = `orders/${docId}/installments/${idx}/payment-proof-${Date.now()}.${safeExt}`;
      const fileRef = storageRef(storage, path);
      const imageExts = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'heic', 'heif']);
      let fallbackContentType = 'application/octet-stream';
      if (safeExt === 'pdf') fallbackContentType = 'application/pdf';
      else if (imageExts.has(safeExt)) {
        const mapped = safeExt === 'jpg' ? 'jpeg' : safeExt;
        fallbackContentType = `image/${mapped}`;
      }
      await uploadBytes(fileRef, file, {
        contentType: file.type || fallbackContentType,
      });
      const url = await getDownloadURL(fileRef);
      const nowIso = new Date().toISOString();
      return {
        url,
        fileName: file.name,
        fileType: file.type || null,
        storagePath: path,
        uploadedAt: nowIso,
        uploadedBy: auth?.currentUser?.uid || null,
      };
    },
    async applyInstallmentProof(row, file) {
      if (!row) return;
      const order = row.order || this.getOrderById(row.docId);
      if (!order) {
        alert('Ordine non trovato.');
        return;
      }
      const docId = String(row.docId || order._id || order.orderId || '');
      if (!docId) return;

      const key = row.key;
      this.setSaving(key, true);
      try {
        const proofPayload = await this.uploadInstallmentProof(row, file);
        let committedOrder = null;
        let committedUpdates = null;
        const orderRef = doc(this.db, 'orders', docId);
        await runTransaction(this.db, async transaction => {
          const orderSnap = await transaction.get(orderRef);
          if (!orderSnap.exists()) {
            throw this.manualInstallmentGuardError('Ordine non trovato.');
          }

          const currentOrder = {
            _id: orderSnap.id,
            ...(orderSnap.data() || {}),
          };
          const plan = Array.isArray(currentOrder.manualInstallments)
            ? currentOrder.manualInstallments.map(inst => ({ ...inst }))
            : [];
          const idx = this.resolveInstallmentIndex(plan, row);
          const target = idx >= 0 ? plan[idx] : null;
          if (!target) {
            throw this.manualInstallmentGuardError('La rata non esiste più. Aggiorna la pagina.');
          }

          const nowIso = new Date().toISOString();
          const existingPaidAt = target.paidAt || target.paid_at || null;
          const nextMethod = this.isPaymentProofMethod(target.method) ? target.method : 'bonifico';
          plan[idx] = {
            ...target,
            method: nextMethod,
            status: 'pagato',
            paidAt: existingPaidAt || nowIso,
            paymentProof: proofPayload,
          };

          const blocked = (
            this.isTerminalOrder(currentOrder)
            || this.isBlockedOrderStatus(currentOrder.status)
            || this.isBlockedOrderStatus(currentOrder.paymentStatus)
          );
          const state = this.manualInstallmentsOrderState(plan);
          const updates = {
            manualInstallments: plan,
            updatedAt: nowIso,
          };
          if (!blocked) {
            updates.status = state.status;
            updates.paymentStatus = state.paymentStatus;
          }

          transaction.update(orderRef, updates);
          committedOrder = currentOrder;
          committedUpdates = updates;
        });

        this.syncCommittedOrder(order, committedOrder, committedUpdates);
      } finally {
        this.setSaving(key, false);
      }
    },
    async setInstallmentAmount(row, nextAmount) {
      if (!row) return;
      const num = Number(nextAmount);
      if (!Number.isFinite(num) || num <= 0) {
        alert('Importo non valido.');
        return;
      }

      const key = row.key;
      this.setSaving(key, true);
      try {
        await this.mutateInstallmentFresh(row, {
          paidMessage: 'L\'importo non è modificabile su una rata già pagata.',
          terminalMessage: 'L\'importo non è modificabile su un ordine annullato.',
          mutate: target => ({
            ...target,
            amount: num,
          }),
        });
      } catch (err) {
        console.error('setInstallmentAmount error:', err);
        alert(
          err?.code === 'manual-installment/guard'
            ? err.message
            : 'Errore durante l’aggiornamento dell’importo.'
        );
      } finally {
        this.setSaving(key, false);
      }
    },
    async addInstallment(group) {
      if (!group || !group.installments || !group.installments.length) return;
      const orderId = group.installments[0].docId;
      const order = this.getOrderById(orderId);
      if (!order) {
        alert('Ordine non trovato.');
        return;
      }
      if (this.isTerminalOrder(order)) {
        alert('Non puoi aggiungere rate a un ordine annullato.');
        return;
      }
      if (!this.canAddInstallment(group)) {
        alert('Compila importo e data per aggiungere una rata.');
        return;
      }
      const newInst = {
        id: `manual_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        amount: Number(this.newInstallment.amount) || 0,
        method: (this.newInstallment.method || '').trim() || null,
        scheduledDate: this.newInstallment.scheduledDate || '',
        status: 'in attesa di pagamento',
        note: (this.newInstallment.note || '').trim(),
        almaInstallments: this.normalizeMethod(this.newInstallment.method) === 'alma'
          ? Number(this.newInstallment.almaInstallments) || null
          : null,
      };

      this.addingInstallment = true;
      try {
        let committedOrder = null;
        let committedUpdates = null;
        const orderRef = doc(this.db, 'orders', String(orderId));
        await runTransaction(this.db, async transaction => {
          const orderSnap = await transaction.get(orderRef);
          if (!orderSnap.exists()) {
            throw this.manualInstallmentGuardError('Ordine non trovato.');
          }

          const currentOrder = {
            _id: orderSnap.id,
            ...(orderSnap.data() || {}),
          };
          if (this.isTerminalOrder(currentOrder)) {
            throw this.manualInstallmentGuardError('Non puoi aggiungere rate a un ordine annullato.');
          }
          const plan = Array.isArray(currentOrder.manualInstallments)
            ? currentOrder.manualInstallments.map(inst => ({ ...inst }))
            : [];
          plan.push({ ...newInst });
          const updates = {
            manualInstallments: plan,
            updatedAt: new Date().toISOString(),
          };
          transaction.update(orderRef, updates);
          committedOrder = currentOrder;
          committedUpdates = updates;
        });

        this.syncCommittedOrder(order, committedOrder, committedUpdates);
        this.newInstallment = { amount: null, method: '', scheduledDate: '', note: '', almaInstallments: '' };
        await this.loadInstallments();
      } catch (err) {
        console.error('addInstallment error:', err);
        alert(
          err?.code === 'manual-installment/guard'
            ? err.message
            : 'Errore durante l’aggiunta della rata.'
        );
      } finally {
        this.addingInstallment = false;
      }
    },
    async removeInstallment(row) {
      if (!row) return;
      const order = row.order || this.getOrderById(row.docId);
      if (!order) return;
      const docId = String(row.docId || order._id || order.orderId || '');
      if (!docId) return;
      const localPlan = Array.isArray(order.manualInstallments)
        ? order.manualInstallments.map(inst => ({ ...inst }))
        : [];
      const localIndex = this.resolveInstallmentIndex(localPlan, row);
      const localTarget = localIndex >= 0 ? localPlan[localIndex] : null;
      if (localTarget && this.isInstallmentPaid(localTarget)) {
        alert('Una rata già pagata non può essere eliminata.');
        return;
      }
      if (this.isTerminalOrder(order)) {
        alert('Le rate storiche di un ordine annullato non possono essere eliminate.');
        return;
      }
      if (!window.confirm('Vuoi eliminare questa rata?')) return;

      const key = row.key;
      this.setSaving(key, true);
      try {
        let committedOrder = null;
        let committedUpdates = null;
        const orderRef = doc(this.db, 'orders', docId);
        await runTransaction(this.db, async transaction => {
          const orderSnap = await transaction.get(orderRef);
          if (!orderSnap.exists()) {
            throw this.manualInstallmentGuardError('Ordine non trovato.');
          }

          const currentOrder = {
            _id: orderSnap.id,
            ...(orderSnap.data() || {}),
          };
          const plan = Array.isArray(currentOrder.manualInstallments)
            ? currentOrder.manualInstallments.map(inst => ({ ...inst }))
            : [];
          const idx = this.resolveInstallmentIndex(plan, row);
          const target = idx >= 0 ? plan[idx] : null;
          if (!target) {
            throw this.manualInstallmentGuardError('La rata non esiste più. Aggiorna la pagina.');
          }
          if (this.isInstallmentPaid(target)) {
            throw this.manualInstallmentGuardError('Una rata già pagata non può essere eliminata.');
          }
          if (this.isTerminalOrder(currentOrder)) {
            throw this.manualInstallmentGuardError(
              'Le rate storiche di un ordine annullato non possono essere eliminate.'
            );
          }

          plan.splice(idx, 1);
          const updates = {
            manualInstallments: plan,
            updatedAt: new Date().toISOString(),
          };
          transaction.update(orderRef, updates);
          committedOrder = currentOrder;
          committedUpdates = updates;
        });

        this.syncCommittedOrder(order, committedOrder, committedUpdates);
        await this.loadInstallments();
      } catch (err) {
        console.error('removeInstallment error:', err);
        alert(
          err?.code === 'manual-installment/guard'
            ? err.message
            : 'Errore durante l’eliminazione della rata.'
        );
      } finally {
        this.setSaving(key, false);
      }
    },
    async setInstallmentNote(row, nextNote) {
      if (!row) return;
      const normalized = (nextNote || '').trim();
      const key = row.key;
      this.setSaving(key, true);
      try {
        await this.mutateInstallmentFresh(row, {
          allowPaid: true,
          allowTerminal: true,
          mutate: target => ({
            ...target,
            note: normalized || '',
          }),
        });
      } catch (err) {
        console.error('setInstallmentNote error:', err);
        alert(
          err?.code === 'manual-installment/guard'
            ? err.message
            : 'Errore durante l’aggiornamento della nota.'
        );
      } finally {
        this.setSaving(key, false);
      }
    },
    async setInstallmentDate(row, nextDate) {
      if (!row) return;
      const normalizedDate = (nextDate || '').trim();
      if (!normalizedDate) {
        alert('Inserisci una data valida.');
        return;
      }

      const parsed = this.parseDueDate(normalizedDate);
      if (!parsed) {
        alert('Data scadenza non valida.');
        return;
      }

      const key = row.key;
      this.setSaving(key, true);
      try {
        await this.mutateInstallmentFresh(row, {
          paidMessage: 'La scadenza non è modificabile su una rata già pagata.',
          terminalMessage: 'La scadenza non è modificabile su un ordine annullato.',
          mutate: target => {
            const targetStatus = this.normalizeStatus(target.status);
            const nextStatus = (
              targetStatus === 'scaduta'
              || targetStatus === 'in attesa di pagamento'
            )
              ? (
                this.isFutureDueDate(normalizedDate)
                  ? 'in attesa di pagamento'
                  : (target.status || 'in attesa di pagamento')
              )
              : target.status;
            return {
              ...target,
              scheduledDate: normalizedDate,
              status: nextStatus,
            };
          },
        });
      } catch (err) {
        console.error('setInstallmentDate error:', err);
        alert(
          err?.code === 'manual-installment/guard'
            ? err.message
            : 'Errore durante l’aggiornamento della scadenza.'
        );
      } finally {
        this.setSaving(key, false);
      }
    },
    triggerCsvPicker() {
      const el = this.$refs.csvInput;
      if (el && el.click) el.click();
    },
    clearImportPreview(preserveMessage = false) {
      this.importState.preview = [];
      this.importState.errors = [];
      this.importState.fileName = '';
      this.importState.stats = { orders: 0, installments: 0, paid: 0 };
      if (!preserveMessage) this.importState.lastResult = '';
      this.csvInputKey += 1; // forza reset input file
    },
    normalizeCsvDate(value) {
      if (!value) return '';
      const raw = String(value).trim();
      if (!raw) return '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
      const match = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
      if (match) {
        const day = Number(match[1]);
        const month = Number(match[2]);
        let year = Number(match[3]);
        if (match[3].length === 2) {
          year = year >= 70 ? 1900 + year : 2000 + year;
        }
        if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return '';
        const dd = String(Math.max(1, Math.min(day, 31))).padStart(2, '0');
        const mm = String(Math.max(1, Math.min(month, 12))).padStart(2, '0');
        const yyyy = String(year).padStart(4, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
      const parsed = new Date(raw);
      if (Number.isNaN(parsed.getTime())) return '';
      return parsed.toISOString().slice(0, 10);
    },
    parseCsvText(text) {
      const cleaned = (text || '').replace(/\r\n/g, '\n').trim();
      const errors = [];
      if (!cleaned) return { rows: [], errors: ['File CSV vuoto.'] };

      const lines = cleaned.split('\n').filter(l => l.trim().length);
      if (!lines.length) return { rows: [], errors: ['File CSV vuoto.'] };

      const headerLine = lines.shift();
      const delimiter = headerLine.includes(';') ? ';' : ',';
      const headers = headerLine.split(delimiter).map(h => h.trim().toLowerCase());

      const getVal = (parts, key) => {
        const idx = headers.indexOf(key);
        if (idx === -1) return '';
        return (parts[idx] || '').trim();
      };

      const rows = [];
      lines.forEach((line, lineIdx) => {
        if (!line.trim()) return;
        const parts = line.split(delimiter);
        const orderId =
          getVal(parts, 'orderid') ||
          getVal(parts, 'subscription_id') ||
          getVal(parts, 'order_id') ||
          getVal(parts, 'ordine') ||
          '';
        const email = getVal(parts, 'email') || getVal(parts, 'mail') || '';
        const amountStr = getVal(parts, 'importo') || getVal(parts, 'amount') || '';
        const rawDue = getVal(parts, 'data_attesa') || getVal(parts, 'due_date') || '';
        const statusRaw = getVal(parts, 'stato') || '';
        const paidField = getVal(parts, 'pagata') || getVal(parts, 'paid') || '';
        const method = getVal(parts, 'metodo') || '';
        const amount = Number((amountStr || '').replace(',', '.'));
        const displayLine = lineIdx + 2; // +1 per l'intestazione

        if (!orderId) {
          errors.push(`Riga ${displayLine}: manca subscription_id / order_id.`);
          return;
        }
        if (!Number.isFinite(amount) || amount <= 0) {
          errors.push(`Riga ${displayLine}: importo non valido (${amountStr || 'vuoto'}).`);
          return;
        }
        const scheduledDate = this.normalizeCsvDate(rawDue);
        if (!scheduledDate) {
          errors.push(`Riga ${displayLine}: data_attesa non valida (${rawDue || 'vuota'}).`);
          return;
        }

        let status = 'in attesa di pagamento';
        const statusLower = (statusRaw || '').toLowerCase();
        if (statusLower.includes('pagato')) status = 'pagato';
        else if (statusLower.includes('scad')) status = 'scaduta';

        const paidLower = (paidField || '').toLowerCase();
        if (paidLower.includes('scad')) status = 'scaduta';

        let paidAt = null;
        const paidDate = this.normalizeCsvDate(paidField);
        if (paidDate) paidAt = `${paidDate}T00:00:00.000Z`;

        rows.push({
          orderId: String(orderId).trim(),
          userEmail: email.trim(),
          amount,
          scheduledDate,
          status,
          paidAt,
          method: method || '',
        });
      });

      return { rows, errors };
    },
    buildImportPreview(rows = []) {
      const map = new Map();
      const stats = { orders: 0, installments: 0, paid: 0 };
      rows.forEach(row => {
        if (!row.orderId) return;
        const key = String(row.orderId).trim();
        if (!map.has(key)) {
          map.set(key, {
            orderId: key,
            userEmail: row.userEmail || '',
            manualInstallments: [],
          });
        }
        const group = map.get(key);
        if (!group.userEmail && row.userEmail) group.userEmail = row.userEmail;
        const statusNorm = this.deriveInstallmentStatus({ status: row.status, scheduledDate: row.scheduledDate });
        group.manualInstallments.push({
          id: `csv_${group.manualInstallments.length + 1}`,
          amount: row.amount,
          method: (row.method || '').trim() || null,
          scheduledDate: row.scheduledDate,
          status: statusNorm || 'in attesa di pagamento',
          paidAt: row.paidAt || null,
          imported: true,
        });
      });

      const preview = Array.from(map.values()).map(group => {
        const plan = group.manualInstallments.slice().sort((a, b) => {
          const ta = this.parseDueDate(a.scheduledDate);
          const tb = this.parseDueDate(b.scheduledDate);
          if (ta && tb) return ta - tb;
          if (ta) return -1;
          if (tb) return 1;
          return 0;
        });
        const paid = plan.filter(inst => this.normalizeStatus(inst.status) === 'pagato').length;
        stats.orders += 1;
        stats.installments += plan.length;
        stats.paid += paid;
        return {
          ...group,
          manualInstallments: plan,
        };
      });

      return { preview, stats };
    },
    async onCsvSelected(event) {
      const file = event?.target?.files?.[0];
      this.importState.errors = [];
      if (!file) return;
      this.importState.fileName = file.name || '';
      this.importState.parsing = true;
      try {
        const text = await file.text();
        const { rows, errors } = this.parseCsvText(text);
        if (errors.length) this.importState.errors = errors;
        const { preview, stats } = this.buildImportPreview(rows);
        this.importState.preview = preview;
        this.importState.stats = stats;
        this.importState.lastResult = '';
      } catch (err) {
        console.error('CSV parse error:', err);
        this.importState.errors = ['Errore nella lettura del file CSV.'];
      } finally {
        this.importState.parsing = false;
        this.csvInputKey += 1; // reset input per scegliere lo stesso file
      }
    },
    async applyCsvImport() {
      if (!this.importState.preview.length) {
        this.importState.errors = ['Nessun dato da importare.'];
        return;
      }
      this.importState.errors = [];
      this.importState.applying = true;
      const nowIso = new Date().toISOString();
      const backup = [];
      let success = 0;
      const errors = [];

      for (const group of this.importState.preview) {
        const docIdFromCsv = String(group.orderId);
        const found = await this.findOrderDoc(docIdFromCsv);
        if (!found) {
          errors.push(`Ordine ${docIdFromCsv}: non trovato, import saltato (nessun nuovo documento creato).`);
          continue;
        }
        const docId = found.docId;
        const docRef = doc(this.db, 'orders', docId);
        const order = found.order;

        const plan = Array.isArray(group.manualInstallments)
          ? group.manualInstallments.map(inst => ({
              ...inst,
              status: inst.status || 'in attesa di pagamento',
              imported: !!inst.imported,
            }))
          : [];

        const state = this.manualInstallmentsOrderState(plan);
        const payload = {
          manualInstallments: plan,
          paymentMethod: 'rate_manuali',
          updatedAt: nowIso,
          status: state.status,
          paymentStatus: state.paymentStatus,
        };
        if (group.userEmail) payload.userEmail = group.userEmail;

        try {
          const backupEntry = await runTransaction(this.db, async transaction => {
            const orderSnap = await transaction.get(docRef);
            if (!orderSnap.exists()) {
              throw this.manualInstallmentGuardError('Ordine non trovato, import saltato.');
            }

            const currentData = orderSnap.data() || {};
            if (this.isTerminalOrder(currentData)) {
              throw this.manualInstallmentGuardError(
                'Ordine annullato o terminale, import saltato per preservare lo storico rateale.'
              );
            }
            const currentPlan = Array.isArray(currentData.manualInstallments)
              ? currentData.manualInstallments
              : [];
            if (currentPlan.some(inst => this.isInstallmentPaid(inst))) {
              throw this.manualInstallmentGuardError(
                'Il piano contiene rate già pagate, import saltato per non sovrascrivere la contabilità.'
              );
            }

            transaction.update(docRef, {
              orderId: currentData.orderId || docId,
              source: currentData.source || 'import_csv',
              importedAt: nowIso,
              ...payload,
            });
            return {
              id: docId,
              created: false,
              manualInstallments: currentPlan.map(inst => ({ ...inst })),
              status: currentData.status || '',
              paymentStatus: currentData.paymentStatus || '',
              paymentMethod: currentData.paymentMethod || '',
              userEmail: currentData.userEmail || currentData.customerEmail || '',
            };
          });
          backup.push(backupEntry);
          if (order) {
            if (this.$set) this.$set(order, 'manualInstallments', plan);
            else order.manualInstallments = plan;
            if (this.$set) this.$set(order, 'status', payload.status);
            else order.status = payload.status;
            order.paymentStatus = payload.paymentStatus;
            order.paymentMethod = payload.paymentMethod;
            if (payload.userEmail) order.userEmail = payload.userEmail;
          }
          success += 1;
        } catch (err) {
          console.error('applyCsvImport order error:', err);
          errors.push(`Ordine ${docId}: ${err?.message || 'errore salvataggio'}`);
        }
      }

      this.lastImportBackup = backup;
      this.importState.applying = false;
      if (errors.length) {
        this.importState.errors = errors;
        this.importState.lastResult = `${success} ordini importati, ${errors.length} errori.`;
        if (success > 0) await this.loadInstallments();
      } else {
        this.importState.lastResult = `${success} ordini importati correttamente.`;
        this.clearImportPreview(true);
        await this.loadInstallments();
      }
    },
    async undoLastImport() {
      if (!this.lastImportBackup || !this.lastImportBackup.length) {
        alert('Nessuna importazione recente da annullare.');
        return;
      }
      if (!window.confirm('Vuoi annullare l’ultima importazione?')) return;

      this.undoingImport = true;
      const nowIso = new Date().toISOString();
      const errors = [];

      for (const entry of this.lastImportBackup) {
        const docId = String(entry.id);
        try {
          const orderRef = doc(this.db, 'orders', docId);
          const payload = entry.created
            ? null
            : {
                manualInstallments: Array.isArray(entry.manualInstallments)
                  ? entry.manualInstallments.map(inst => ({ ...inst }))
                  : [],
                status: entry.status || 'in attesa di pagamento',
                paymentStatus: entry.paymentStatus || 'pending',
                paymentMethod: entry.paymentMethod || 'rate_manuali',
                updatedAt: nowIso,
              };
          if (payload && entry.userEmail) payload.userEmail = entry.userEmail;

          await runTransaction(this.db, async transaction => {
            const orderSnap = await transaction.get(orderRef);
            if (!orderSnap.exists()) {
              if (entry.created) return;
              throw this.manualInstallmentGuardError('Ordine non trovato.');
            }
            const currentOrder = {
              _id: orderSnap.id,
              ...(orderSnap.data() || {}),
            };
            if (this.isTerminalOrder(currentOrder)) {
              throw this.manualInstallmentGuardError(
                'Ripristino saltato: l’ordine è stato annullato dopo l’import.'
              );
            }
            const currentPlan = Array.isArray(currentOrder.manualInstallments)
              ? currentOrder.manualInstallments
              : [];
            if (currentPlan.some(inst => this.isInstallmentPaid(inst))) {
              throw this.manualInstallmentGuardError(
                'Ripristino saltato: il piano contiene rate già pagate.'
              );
            }
            if (entry.created) transaction.delete(orderRef);
            else transaction.update(orderRef, payload);
          });

          if (entry.created) {
            const idx = this.orders.findIndex(o => (o._id || o.orderId) === docId);
            if (idx >= 0) this.orders.splice(idx, 1);
          } else {
            const order = this.getOrderById(docId);
            if (order) {
              if (this.$set) this.$set(order, 'manualInstallments', payload.manualInstallments);
              else order.manualInstallments = payload.manualInstallments;
              if (this.$set) this.$set(order, 'status', payload.status);
              else order.status = payload.status;
              order.paymentStatus = payload.paymentStatus;
              order.paymentMethod = payload.paymentMethod;
              if (payload.userEmail) order.userEmail = payload.userEmail;
            }
          }
        } catch (err) {
          console.error('undoLastImport error:', err);
          errors.push(`Ordine ${docId}: ${err?.message || 'errore ripristino'}`);
        }
      }

      this.undoingImport = false;
      this.lastImportBackup = null;
      if (errors.length) {
        alert(`Ripristino completato con errori:\n${errors.join('\n')}`);
      } else {
        await this.loadInstallments();
        alert('Ultima importazione annullata.');
      }
    },
    async normalizeFutureInstallments() {
      if (this.fixingFuture) return;
      if (!this.orders.length) {
        await this.loadInstallments();
      }
      if (!this.orders.length) {
        alert('Nessuna rata manuale da normalizzare.');
        return;
      }
      if (!window.confirm('Vuoi normalizzare gli stati delle rate future non pagate? Le rate già pagate resteranno invariate.')) {
        return;
      }

      this.fixingFuture = true;
      this.lastFixResult = '';
      let ordersUpdated = 0;
      let installmentsUpdated = 0;
      const errors = [];

      for (const order of this.orders) {
        const docId = order._id || order.orderId;
        if (!docId) continue;

        try {
          const orderRef = doc(this.db, 'orders', String(docId));
          const result = await runTransaction(this.db, async transaction => {
            const orderSnap = await transaction.get(orderRef);
            if (!orderSnap.exists()) {
              throw this.manualInstallmentGuardError('Ordine non trovato.');
            }

            const currentOrder = {
              _id: orderSnap.id,
              ...(orderSnap.data() || {}),
            };
            if (this.isTerminalOrder(currentOrder)) {
              return { changedCount: 0, currentOrder, updates: null };
            }

            const plan = Array.isArray(currentOrder.manualInstallments)
              ? currentOrder.manualInstallments.map(inst => ({ ...inst }))
              : [];
            let changedCount = 0;
            const normalizedPlan = plan.map(inst => {
              // Una rata contabilizzata è immutabile anche durante la normalizzazione bulk.
              if (this.isInstallmentPaid(inst)) return inst;
              const nextStatus = this.deriveInstallmentStatus(inst, currentOrder);
              const currentStatus = this.normalizeStatus(inst.status || 'in attesa di pagamento');
              if (nextStatus !== currentStatus) {
                changedCount += 1;
                return { ...inst, status: nextStatus };
              }
              return inst;
            });
            if (!changedCount) {
              return { changedCount: 0, currentOrder, updates: null };
            }

            const nowIso = new Date().toISOString();
            const state = this.manualInstallmentsOrderState(normalizedPlan);
            const updates = {
              manualInstallments: normalizedPlan,
              updatedAt: nowIso,
              status: state.status,
              paymentStatus: state.paymentStatus,
            };
            transaction.update(orderRef, updates);
            return { changedCount, currentOrder, updates };
          });

          if (!result?.changedCount || !result.updates) continue;
          installmentsUpdated += result.changedCount;
          this.syncCommittedOrder(order, result.currentOrder, result.updates);
          ordersUpdated += 1;
        } catch (err) {
          console.error('normalizeFutureInstallments error:', err);
          errors.push(`Ordine ${docId}: ${err?.message || 'errore aggiornamento'}`);
        }
      }

      this.fixingFuture = false;
      if (errors.length) {
        this.lastFixResult = `Aggiornati ${ordersUpdated} ordini, ${installmentsUpdated} rate corrette, ${errors.length} errori.`;
        alert(`Normalizzazione completata con errori:\\n${errors.join('\\n')}`);
      } else {
        this.lastFixResult = `Aggiornati ${ordersUpdated} ordini, ${installmentsUpdated} rate corrette.`;
      }
      if (ordersUpdated > 0) {
        await this.loadInstallments();
      }
    },
    async toggleGroupStop(group) {
      if (!group) return;
      const docIds = Array.from(new Set((group.installments || []).map(r => r.docId).filter(Boolean)));
      if (!docIds.length) return;
      const nextStop = !group.hasStop;
      const nowIso = new Date().toISOString();
      this.setTogglingStop(group.key, true);
      const errors = [];
      let updated = 0;

      for (const docId of docIds) {
        try {
          await updateDoc(doc(this.db, 'orders', String(docId)), {
            manualInstallmentsStop: nextStop,
            updatedAt: nowIso,
          });
          const order = this.getOrderById(docId);
          if (order) {
            if (this.$set) this.$set(order, 'manualInstallmentsStop', nextStop);
            else order.manualInstallmentsStop = nextStop;
          }
          updated += 1;
        } catch (err) {
          console.error('toggleGroupStop error:', err);
          errors.push(`Ordine ${docId}: ${err?.message || 'errore aggiornamento'}`);
        }
      }

      this.setTogglingStop(group.key, false);
      if (errors.length) {
        alert(`Aggiornamento STOP completato con errori:\n${errors.join('\n')}`);
      } else if (updated === 0) {
        alert('Nessun ordine aggiornato.');
      }
    },
    applyFilters() {
      // I filtri sono reattivi; questo metodo esiste solo per l'evento Enter.
    },
      resetFilters() {
        this.filters = { userState: '', search: '', fromDate: '', toDate: '', studio: '', stop: 'exclude', operationalHold: 'exclude' };
      },
    isGroupOpen(key) {
      if (!key) return false;
      return !!this.openGroups[key];
    },
    toggleGroup(key) {
      if (!key) return;
      const next = !this.openGroups[key];
      if (this.$set) this.$set(this.openGroups, key, next);
      else this.openGroups[key] = next;
    },
  },
};
</script>

<style scoped>
.card-surface {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  padding: 18px 20px;
  margin-top: 18px;
}

.toolbar-card {
  margin-top: 24px;
}

.date-input {
  width: 85%!important;
}

.subtitle {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 0.95rem;
  font-style: italic;
}

.hidden { display: none; }

.import-card {
  border: 1px dashed #cbd5e1;
}

.import-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.import-copy h3 {
  margin: 0;
  font-size: 1.05rem;
}

.import-copy .muted {
  margin-top: 4px;
}

.import-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
  margin-left: auto;
}

.import-preview {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.import-summary {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  font-weight: 600;
  color: #0f172a;
}

.import-result {
  margin-top: 6px;
  color: #0f172a;
  font-weight: 600;
}

.import-errors .error-state.small {
  font-size: 0.9rem;
  margin-top: 6px;
}

.preview-table {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
}

.preview-row {
  display: grid;
  grid-template-columns: 1.1fr 1.4fr 0.8fr 0.8fr 1fr 1fr;
  gap: 10px;
  padding: 10px 12px;
  align-items: center;
  background: #fff;
  border-top: 1px solid #e2e8f0;
}

.preview-row.header {
  background: #f1f5f9;
  border-top: none;
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.8rem;
  color: #475569;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
}

.toolbar-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 0 0 auto;
}

.toolbar-group--md { width: 200px; }
.toolbar-group--lg { width: 280px; }
.toolbar-group.grow {
  flex: 1 1 320px;
  min-width: 260px;
}

.toolbar-label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
}

.toolbar-actions {
  display: flex;
  gap: 12px;
  flex: 0 0 auto;
  align-items: center;
  margin-left: auto;
}

.input {
  width: 100%;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #0f172a;
  transition: border 0.2s ease, box-shadow 0.2s ease;
}

.input:focus {
  border-color: #2563eb;
  background: #fff;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
  outline: none;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.btn.primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: #fff;
}

.btn.ghost {
  background: #f1f5f9;
  color: #1e293b;
  border-color: #e2e8f0;
}

.btn.tiny {
  font-size: 0.78rem;
  padding: 6px 10px;
}

.filters-card {
  padding-bottom: 14px;
}

.filters-note {
  margin-top: 10px;
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  color: #475569;
  font-size: 0.9rem;
}

.filters-note .muted {
  color: #94a3b8;
}

.accordion-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.accordion-item {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
  transition: border 0.2s ease, box-shadow 0.2s ease;
}

.accordion-item.has-overdue {
  border-color: #fecdd3;
  box-shadow: 0 14px 30px rgba(248, 113, 113, 0.18);
}

.accordion-item.has-overdue .toggle-title,
.accordion-item.has-overdue .toggle-subtitle {
  color: #b91c1c;
}

.accordion-item.has-stop {
  border-color: #fbbf24;
  box-shadow: 0 14px 30px rgba(245, 158, 11, 0.18);
}

.accordion-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 14px 16px;
  background: #f8fafc;
  border: none;
  cursor: pointer;
  color: #0f172a;
  transition: background 0.18s ease, transform 0.18s ease;
}

.accordion-toggle:hover {
  background: #fff;
}

.accordion-item.has-overdue .accordion-toggle {
  background: #fff1f2;
}

.accordion-toggle .material-icons {
  font-size: 22px;
  color: #475569;
}

.accordion-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 4px;
}

.accordion-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.toggle-cols {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: center;
  gap: 12px;
  width: 100%;
}
.toggle-col {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}
.toggle-col--actions {
  justify-content: center;
  align-items: center;
  flex: 0 0 auto;
}
.toggle-col--meta {
  align-items: flex-end;
}
.toggle-col--chevron {
  align-items: center;
  justify-content: center;
}
.toggle-meta-right {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.toggle-chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.toggle-copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.toggle-title {
  font-weight: 700;
  font-size: 0.95rem;
}
.toggle-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.email-profile-btn {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: #e0e7ff;
  color: #1e3a8a;
  cursor: pointer;
  transition: background 0.18s ease, transform 0.18s ease;
}
.email-profile-btn:hover:not(:disabled) {
  background: #c7d2fe;
  transform: translateY(-1px);
}
.email-profile-btn:disabled {
  opacity: 0.5;
  cursor: default;
  transform: none;
}

.toggle-subtitle {
  font-size: 0.85rem;
  color: #64748b;
}
.toggle-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.82rem;
  border: 1px solid transparent;
}
.product-pill {
  background: #f1f5f9;
  border-color: #e2e8f0;
  color: #0f172a;
}
.amount-pill {
  background: #e0f2fe;
  border-color: #bae6fd;
  color: #075985;
}
.chevron-icon {
  color: #475569;
}

.accordion-panel {
  padding: 14px 16px 16px;
  background: #fff;
  border-top: 1px solid #e2e8f0;
}

.installments-grid {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.installment-row {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1fr 1fr 1.25fr;
  gap: 12px;
  padding: 12px 10px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  align-items: center;
  transition: border 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.installment-row.header {
  background: #eef2ff;
  border: 1px solid #e0e7ff;
  text-transform: uppercase;
  font-size: 0.85rem;
  font-weight: 700;
  color: #475569;
}

.installment-row:not(.header):hover {
  border-color: #cbd5f5;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
  background: #fff;
}

.installment-row.overdue {
  border-color: #fca5a5;
  background: #fff7f7;
  box-shadow: 0 12px 26px rgba(239, 68, 68, 0.08);
}

.installment-col .muted {
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.2;
}

.installment-col .strong {
  font-weight: 700;
  color: #0f172a;
}

.method-picker {
  margin-top: 6px;
}
.alma-installments-row {
  margin-top: 6px;
}
.proof-row {
  margin-top: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.proof-link {
  font-size: 0.78rem;
  color: #0f172a;
  text-decoration: underline;
}

.inline-select {
  padding: 9px 12px;
  min-width: 150px;
  max-width: 190px;
}
.amount-input {
  max-width: 70%;
}
.note-input {
  min-width: 180px;
  max-width: 220px;
}
.date-edit .date-input {
  width: 100%;
  min-width: 150px;
}
.add-installment-row {
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px dashed #e2e8f0;
}
.add-installment-title {
  font-weight: 700;
  margin-bottom: 6px;
}
.add-installment-form {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr auto;
  gap: 10px;
  align-items: center;
}
.add-installment-form .input {
  max-width: 89%;
}

.installment-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.alma-link-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}
.icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}
.icon-btn .material-icons {
  font-size: 18px;
}
.icon-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(15, 23, 42, 0.12);
}
.icon-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  box-shadow: none;
}
.icon-btn.primary {
  background: #fff;
  color: #16a34a;
  border-color: #bbf7d0;
}
.icon-btn.danger {
  background: #fff;
  color: #b91c1c;
  border-color: #fecaca;
}

.align-right {
  text-align: right;
}

.hint {
  margin-top: 4px;
  color: #94a3b8;
  font-size: 0.82rem;
}

.status-chip {
  padding: 4px 10px;
  border-radius: 999px;
  background: #e0e7ff;
  color: #312e81;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: capitalize;
  white-space: nowrap;
}

.status-chip--pagato { background: #dcfce7; color: #166534; }
.status-chip--scaduta { background: #fee2e2; color: #b91c1c; }
.status-chip--in-attesa-di-pagamento { background: #fef3c7; color: #92400e; }
.status-chip--annullata { background: #e2e8f0; color: #475569; }
.status-chip--unknown { background: #e2e8f0; color: #475569; }

.chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.74rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.chip.danger {
  background: #fee2e2;
  color: #b91c1c;
}
.chip.info {
  background: #e0f2fe;
  color: #075985;
}
.chip.success {
  background: #dcfce7;
  color: #166534;
}
.chip.stop {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fbbf24;
}

.stop-btn {
  background: #fff7ed;
  color: #92400e;
  border-color: #fed7aa;
}
.stop-btn:hover:not(:disabled) {
  background: #ffedd5;
}

.error-state {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecdd3;
  padding: 14px 16px;
  border-radius: 14px;
}

.empty-state {
  text-align: center;
  padding: 32px;
  color: #64748b;
  margin-top: 8px;
}

.muted {
  color: #94a3b8;
}

@media (max-width: 900px) {
  .installment-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .preview-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .import-row {
    flex-direction: column;
  }
  .import-actions {
    margin-left: 0;
  }
}
</style>
